import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { HiBars3, HiChevronDown, HiChevronLeft, HiChevronRight, HiMagnifyingGlass } from 'react-icons/hi2'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import { AttentionDot, HubLinkMenuItems, type HubLinkRenderProps } from './HubLinkDirectory'
import type { HubLinkSection } from './hubLinks'
import './hub-links.css'

interface Props extends HubLinkRenderProps {
  sections: HubLinkSection[]
  loading?: boolean
  triggerClassName: string
  onSearch: () => void
}

const GAP = 2

/**
 * One small menu per link group, so the header shows what exists without
 * opening everything at once. Groups that don't fit fold into "More".
 */
export default function HubGroupNav({ sections, loading = false, triggerClassName, onSearch, ...render }: Props) {
  const location = useLocation()
  const navRef = useRef<HTMLElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(sections.length)
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openedByHover = useRef(false)
  const layoutKey = sections.map(section => `${section.group.id}:${section.links.length}`).join('|')

  const cancelClose = () => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current)
    closeTimer.current = null
  }
  const openOnHover = (id: string) => {
    cancelClose()
    openedByHover.current = true
    setOpenGroup(id)
  }
  const scheduleClose = (id: string) => {
    cancelClose()
    // Leave time to cross the gap between the button and its menu.
    closeTimer.current = setTimeout(() => {
      setOpenGroup(current => current === id ? null : current)
      closeTimer.current = null
    }, 180)
  }
  useEffect(() => cancelClose, [])
  useEffect(() => {
    cancelClose()
    setOpenGroup(null)
  }, [location.pathname])

  // Fit as many groups as the header allows; measure hidden copies so the
  // visible buttons never jump while they are being counted.
  useLayoutEffect(() => {
    const nav = navRef.current
    const measure = measureRef.current
    if (!nav || !measure) return
    const compute = () => {
      const widths = [...measure.children].map(child => (child as HTMLElement).offsetWidth)
      const moreWidth = widths.pop() || 0
      const available = nav.clientWidth
      // Not laid out (hidden, or no layout engine): keep every group rather than guess.
      if (!available) {
        setVisibleCount(widths.length)
        return
      }
      let used = 0
      let count = 0
      for (let index = 0; index < widths.length; index++) {
        const withItem = used + (index ? GAP : 0) + widths[index]
        const reserve = index === widths.length - 1 ? 0 : GAP + moreWidth
        if (withItem + reserve > available) break
        used = withItem
        count = index + 1
      }
      setVisibleCount(count)
    }
    compute()
    const observer = new ResizeObserver(compute)
    observer.observe(nav)
    void document.fonts?.ready.then(compute)
    return () => observer.disconnect()
  }, [layoutKey, loading])

  const visible = sections.slice(0, visibleCount)
  const overflow = sections.slice(visibleCount)
  const hasAttention = (section: HubLinkSection) => section.links.some(link => render.needsAttention?.(link))
  const isCurrent = (section: HubLinkSection) => section.links.some(link => render.isActive?.(link))

  return <nav ref={navRef} className="hub-group-nav" aria-label="Main">
    <div ref={measureRef} className="hub-group-nav-measure" aria-hidden="true">
      {sections.map(section => <span key={section.group.id} className={triggerClassName}>
        {section.group.label}{hasAttention(section) && <span className="hub-attention-dot" />}<HiChevronDown className="h-3.5 w-3.5" />
      </span>)}
      <span className={triggerClassName}><HiBars3 className="h-5 w-5" />Menu<HiChevronDown className="h-3.5 w-3.5" /></span>
    </div>

    {!loading && visible.map((section, index) => {
      const { group } = section
      return <DropdownMenu key={group.id} modal={false} open={openGroup === group.id}
        onOpenChange={open => {
          cancelClose()
          setOpenGroup(current => open ? group.id : current === group.id ? null : current)
        }}>
        <DropdownMenuTrigger asChild>
          <button type="button" className={`${triggerClassName} primary-nav-item-reveal hub-group-trigger`}
            data-current={isCurrent(section) || undefined} style={{ animationDelay: `${index * 50}ms` }}
            aria-label={`${group.label}${hasAttention(section) ? ', something needs your attention' : ''}`}
            onPointerEnter={event => { if (event.pointerType === 'mouse') openOnHover(group.id) }}
            onPointerLeave={event => { if (event.pointerType === 'mouse') scheduleClose(group.id) }}
            onPointerDown={event => {
              if (event.pointerType === 'mouse') event.preventDefault()
              else openedByHover.current = false
            }}
            onKeyDown={() => { openedByHover.current = false }}>
            {group.label}
            {hasAttention(section) && <span className="hub-attention-dot" aria-hidden="true" />}
            <HiChevronDown className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={8} collisionPadding={12} className={`hub-dir hub-group-menu hub-tone-${group.id}`}
          onPointerEnter={cancelClose}
          onPointerLeave={event => { if (event.pointerType === 'mouse') scheduleClose(group.id) }}
          onOpenAutoFocus={event => { if (openedByHover.current) event.preventDefault() }}
          onCloseAutoFocus={event => { if (openedByHover.current) event.preventDefault() }}
          onKeyDown={() => { openedByHover.current = false }}>
          <p className="hub-group-menu-description">{group.description}</p>
          <HubLinkMenuItems links={section.links} {...render} />
        </DropdownMenuContent>
      </DropdownMenu>
    })}

    {!loading && overflow.length > 0 &&
      <OverflowMenu sections={overflow} label={visible.length ? 'More' : 'Menu'} triggerClassName={triggerClassName}
        onSearch={onSearch} hasAttention={hasAttention} {...render} />}
  </nav>
}

/** Groups first, then the chosen group's links: never the whole list at once. */
function OverflowMenu({ sections, label, triggerClassName, onSearch, hasAttention, ...render }: HubLinkRenderProps & {
  sections: HubLinkSection[]; label: 'More' | 'Menu'; triggerClassName: string; onSearch: () => void
  hasAttention: (section: HubLinkSection) => boolean
}) {
  const [chosen, setChosen] = useState<string | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const section = sections.find(item => item.group.id === chosen)
  useEffect(() => {
    // Keep keyboard focus inside the menu when switching between levels.
    const frame = requestAnimationFrame(() => contentRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus())
    return () => cancelAnimationFrame(frame)
  }, [chosen])
  const anyAttention = sections.some(hasAttention)
  return <DropdownMenu modal={false} onOpenChange={open => { if (!open) setChosen(null) }}>
    <DropdownMenuTrigger asChild>
      <button type="button" className={`${triggerClassName} hub-group-trigger`}
        aria-label={`${label}${anyAttention ? ', something needs your attention' : ''}`}>
        {label === 'Menu' && <HiBars3 className="h-5 w-5" />}
        {label}
        {anyAttention && <span className="hub-attention-dot" aria-hidden="true" />}
        <HiChevronDown className="h-3.5 w-3.5" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent ref={contentRef} align="start" sideOffset={8} collisionPadding={12} className="hub-dir hub-group-menu hub-overflow-menu">
      {section ? <>
        <DropdownMenuItem className="hub-menu-back" onSelect={event => { event.preventDefault(); setChosen(null) }}>
          <HiChevronLeft aria-hidden="true" />{label === 'Menu' ? 'All groups' : 'More'}
        </DropdownMenuItem>
        <DropdownMenuLabel className="hub-overflow-heading">{section.group.label}</DropdownMenuLabel>
        <HubLinkMenuItems links={section.links} {...render} />
      </> : sections.map(item => <DropdownMenuItem key={item.group.id} className={`hub-overflow-group hub-tone-${item.group.id}`}
        onSelect={event => { event.preventDefault(); setChosen(item.group.id) }}>
        <span className="hub-group-icon"><item.group.icon aria-hidden="true" /></span>
        <span className="hub-row-label">{item.group.label}</span>
        {hasAttention(item) && <AttentionDot />}
        <HiChevronRight className="hub-row-chevron" aria-hidden="true" />
      </DropdownMenuItem>)}
      <DropdownMenuSeparator className="hub-menu-separator" />
      <DropdownMenuItem className="hub-overflow-search" onSelect={onSearch}>
        <HiMagnifyingGlass aria-hidden="true" />Search Duo Hub
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
}
